import {
  Avatar,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import {
  LeftArrow,
  LeftArrowWhite,
  PurpleClose,
  PurpleWarning,
  SuccessIcon,
  VIconCircle,
} from 'Images';
import { ContactUsSegment } from 'Modules/Equipment/EquipmentMain/EquipmentNotAllowed/ContactUsSegment';
import { useNavigate } from 'react-router-dom';

interface ISuccessOrderDialog {
  open: boolean;
  handleClose: () => void;
  renewPrescription?: string[];
  numberOfItems: number;
  linkLabel?: string;
  link?: string;
}

const maxItemsPerOrder = 30;
export const SuccessOrderDialog = (props: ISuccessOrderDialog) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Equipment
  ).successOrderDialog;
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  const listIcon = (
    <Avatar
      alt=''
      src={VIconCircle}
      sx={{
        height: '20px',
        width: '20px',
        mr: '10px',
      }}
    />
  );

  return (
    <Dialog
      PaperProps={{
        sx: {
          overflowY: 'visible',
          borderRadius: '24px',
          mt: isDesktop ? '44px' : '90px',
          backgroundColor: 'primary.shade_100',
          p: 0,
          ml: 2,
          mr: 2,
        },
      }}
      sx={{
        margin: 'auto',
        maxWidth: '1200px',
        height: isDesktop ? 'fit-content' : undefined,
        '.MuiDialogContent-root': {
          p: isDesktop ? '264px 273px 285pxs' : undefined,
        },
      }}
      maxWidth='md'
      fullWidth={isDesktop}
      open={props.open}
      onClose={props.link ? () => navigate(`${props.link}`) : props.handleClose}
      aria-labelledby={'success-order-dialog-title'}
      aria-describedby={'success-order-dialog-content'}
    >
      <IconButton
        onClick={
          props.link ? () => navigate(`${props.link}`) : props.handleClose
        }
        sx={{
          position: 'absolute',
          right: isDesktop ? '24px' : '13px',
          top: isDesktop ? '24px' : '13px',
          zIndex: 1,
        }}
      >
        <Avatar
          alt={generalDictionary.closeWindow}
          src={PurpleClose}
          sx={{
            width: isDesktop ? '42px' : '34px',
            height: isDesktop ? '42px' : '34px',
          }}
        />
      </IconButton>

      <DialogContent
        sx={{
          maxHeight: isDesktop ? '90vh' : '84vh',
        }}
        id='success-order-dialog-content'
      >
        <Stack alignItems='center' mt={isDesktop ? 5 : '0'} p={0}>
          <Avatar
            alt=''
            src={SuccessIcon}
            sx={{
              height: isDesktop ? '92px' : '75px',
              width: isDesktop ? '142px' : '110px',
              m: '0 auto',
              mb: '16px',
            }}
          />
          <Typography
            textAlign='center'
            m={isDesktop ? '0 0 12px' : '0 10px 10px'}
            lineHeight={isDesktop ? undefined : '28px'}
            id='success-order-dialog-title'
            variant='h3'
          >
            {dictionary.title}
          </Typography>
          {isDesktop ? (
            <Stack
              flexDirection='row'
              alignItems='center'
              justifyContent='center'
              gap='6px'
              m={isDesktop ? '0 0 32px' : '5px 0 21px'}
            >
              <Typography variant='body3'>
                {dictionary.followOrderHere[0]}
              </Typography>
              <Link
                underline='none'
                href={props.link || '/equipment'}
                variant='body3'
              >
                {props.linkLabel || dictionary.followOrderHere[1]}
              </Link>
              <Avatar
                alt=''
                src={LeftArrow}
                sx={{ width: '21px', height: '21px', top: '2px', right: '4px' }}
              />
            </Stack>
          ) : (
            <Stack
              alignItems='center'
              justifyContent='center'
              m={isDesktop ? '0 0 40px' : '5px 0 21px'}
            >
              <Typography variant='body3'>
                {dictionary.followOrderHereMobile[0]}
              </Typography>
              <Stack
                flexDirection='row'
                alignItems='center'
                justifyContent='center'
                gap='6px'
              >
                <Button
                  endIcon={
                    <Avatar
                      src={LeftArrow}
                      sx={{
                        width: '18px',
                        height: '18px',
                        top: '2px',
                        right: '4px',
                      }}
                    />
                  }
                  href={props.link || '/equipment'}
                  sx={{ fontSize: '18px', p: 0 }}
                >
                  {props.linkLabel || dictionary.followOrderHereMobile[2]}
                </Button>
              </Stack>
            </Stack>
          )}
          <Typography
            textAlign='center'
            fontWeight={700}
            paragraph
            m={isDesktop ? '0 0 8px' : ' 0px'}
            component='h3'
          >
            {dictionary.date} {format(new Date(Date.now()), 'dd.MM.yy')} |{' '}
            {format(new Date(Date.now()), 'HH:mm')}
          </Typography>
          <Typography
            textAlign='center'
            fontWeight={400}
            variant='body3'
            paragraph
            mb={'16px'}
            component='h4'
          >
            {dictionary.youOrder} {props.numberOfItems} {dictionary.items}
          </Typography>
          {props.numberOfItems >= maxItemsPerOrder && (
            <Stack
              flexDirection='row'
              alignItems={isDesktop ? 'center' : 'flex-start'}
              justifyContent='center'
              gap='8px'
              mb={isDesktop ? '32px' : '16px'}
            >
              <Avatar
                src={PurpleWarning}
                alt=''
                sx={{
                  width: '22px',
                  height: '22px',
                  mt: isDesktop ? undefined : '3px',
                }}
              />
              <Typography>{dictionary.over30Items}</Typography>
            </Stack>
          )}

          <Container
            disableGutters
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              maxWidth: '550px !important',
              border: '1px solid',
              borderColor: 'primary.shade_200',
            }}
          >
            <Typography
              sx={{
                width: '100%',
                textAlign: 'center',
                padding: '14px 0',
              }}
              variant='body1'
              component={'h2'}
            >
              {dictionary.whatNext}
            </Typography>
            <ul
              style={{
                margin: 0,
                padding: '8px 16px 12px',
              }}
            >
              <Stack flexDirection={'row'} component='li'>
                {listIcon}
                <Typography sx={{ mb: 1 }} variant='body6' paragraph>
                  {dictionary.list1}
                </Typography>
              </Stack>

              <Stack flexDirection={'row'} component='li'>
                {listIcon}
                <Typography sx={{ mb: 1 }} variant='body6' paragraph>
                  {dictionary.list2}
                </Typography>
              </Stack>
              <Stack flexDirection={'row'} component='li'>
                {listIcon}
                <Typography sx={{ mb: 1 }} variant='body6' paragraph>
                  {dictionary.list3}
                </Typography>
              </Stack>
            </ul>
          </Container>

          <Button
            sx={{
              height: '46px',
              width: isDesktop ? '200px' : '142px',
              fontSize: '18px',
              fontWeight: 'bold',
              lineHeight: isDesktop ? '24px' : '26px',
              mt: '40px',
            }}
            variant={'contained'}
            onClick={() =>
              props.link ? navigate(props.link) : navigate('/Equipment')
            }
            endIcon={
              <Avatar
                src={LeftArrowWhite}
                sx={{ width: '16px', height: '16px' }}
              />
            }
          >
            {isDesktop ? dictionary.finish : generalDictionary.finish}
          </Button>
        </Stack>
        <Stack m={'24px 0'}>
          <ContactUsSegment />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
